import { z } from 'zod';

import { ROLES, type Permission, type RoleName } from './permissions';

/**
 * The authenticated user, as returned by `GET /api/v1/admin/me`.
 *
 * Parsed rather than cast: this object decides what the whole panel renders, so
 * a backend change that drops `permissions` should fail loudly at the boundary
 * instead of quietly hiding every menu item.
 */
export const authUserSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
  email: z.email(),
  avatar_url: z.string().nullable().default(null),
  locale: z.string().default('en'),
  roles: z.array(z.string()).default([]),
  permissions: z.array(z.string()).default([]),
  assigned_locale_ids: z.array(z.number().int().positive()).default([]),
  assigned_locales: z
    .array(
      z.object({
        id: z.number().int().positive(),
        code: z.string(),
        name: z.string(),
      }),
    )
    .default([]),
  last_login_at: z.string().nullable().default(null),
});

export type AuthUser = z.infer<typeof authUserSchema>;

export const loginResponseSchema = z.object({
  token: z.string().min(1),
  expires_at: z.string().nullable().default(null),
  user: authUserSchema,
});

export type LoginResponse = z.infer<typeof loginResponseSchema>;

export const loginFormSchema = z.object({
  email: z.email({ error: 'Enter a valid email address.' }),
  password: z.string().min(1, 'Enter your password.'),
  remember: z.boolean().default(false),
});

export type LoginFormValues = z.input<typeof loginFormSchema>;

/**
 * Super Admin is granted everything without enumerating permissions, matching
 * `Gate::before` on the API side. Keeping the same rule here stops the UI from
 * hiding features the API would happily allow.
 */
export function userHasPermission(user: AuthUser | null, permission: Permission): boolean {
  if (!user) {
    return false;
  }

  if (user.roles.includes(ROLES.superAdmin)) {
    return true;
  }

  return user.permissions.includes(permission);
}

export function userHasAnyPermission(
  user: AuthUser | null,
  permissions: readonly Permission[],
): boolean {
  return permissions.some((permission) => userHasPermission(user, permission));
}

export function userHasRole(user: AuthUser | null, role: RoleName): boolean {
  return user?.roles.includes(role) ?? false;
}
